const KeycloakLib = require("keycloak-js");
const Keycloak = KeycloakLib.default || KeycloakLib;

const keycloak = new Keycloak({
  url: process.env.REACT_APP_KEYCLOAK_URL || "/auth",
  realm: process.env.REACT_APP_KEYCLOAK_REALM || "eudr",
  clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID || "eudr-webui",
});

const KEYCLOAK_INIT_TIMEOUT_MS = 45_000;
/** Keep in sync with i18n.LANGUAGE_STORAGE_KEY — avoid importing i18n (circular risk). */
const LANGUAGE_STORAGE_KEY = "eudr.language";

let initPromise: Promise<boolean> | undefined;

function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      }
    );
  });
}

function readStoredAppLanguage(): string {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored) return stored;
  } catch {
    /* ignore */
  }
  if (typeof document !== "undefined" && document.documentElement?.lang) {
    return document.documentElement.lang;
  }
  return "en";
}

/**
 * Map app i18n codes to Keycloak / OIDC ui_locales values.
 * keycloak-js sends `locale` as the `ui_locales` query param (OIDC 3.1.2.1).
 * Never use login-required / auto-login here — only interactive CTAs call login().
 */
export function toKeycloakLocale(lang?: string | null): string {
  const raw = (lang || "en").split("-")[0].toLowerCase();
  if (raw === "zh") return "zh-CN";
  return raw || "en";
}

/** Login options including ui_locales from the current app language. */
export function buildKeycloakLoginOptions(redirectUri: string, lang?: string | null) {
  return {
    redirectUri,
    locale: toKeycloakLocale(lang ?? readStoredAppLanguage()),
  };
}

/** Options passed to keycloak-js init (exported for unit tests). */
export function buildKeycloakInitOptions(
  origin: string | undefined = typeof window !== "undefined" ? window.location.origin : undefined
) {
  // checkLoginIframe must stay false: browsers block 3rd-party cookie iframes and
  // keycloak-js otherwise fatals with "Timeout when waiting for 3rd party check iframe message."
  // silentCheckSsoRedirectUri still triggers a same-origin 3p-cookies probe under /auth —
  // LB must allow SAMEORIGIN framing (see eudr-lb/nginx.prod.conf).
  // onLoad MUST remain check-sso (never login-required) so public `/` never redirects to Keycloak.
  return {
    onLoad: "check-sso" as const,
    checkLoginIframe: false as const,
    pkceMethod: "S256" as const,
    ...(origin ? { silentCheckSsoRedirectUri: `${origin}/silent-check-sso.html` } : {}),
  };
}

/**
 * Silent check-sso: public routes must not force a login wall (US-806).
 * Does not call keycloak.login() — only RequireAuth / loginToApp do that on user intent.
 */
export async function initKeycloak(): Promise<boolean> {
  if (!initPromise) {
    initPromise = withTimeout<boolean>(
      keycloak.init(buildKeycloakInitOptions()) as Promise<boolean>,
      KEYCLOAK_INIT_TIMEOUT_MS,
      "Keycloak initialization timed out"
    ).catch((err) => {
      initPromise = undefined;
      throw err;
    });
  }
  return initPromise;
}

export async function ensureFreshToken(): Promise<string | undefined> {
  if (!keycloak.authenticated) {
    return undefined;
  }
  await keycloak.updateToken(30);
  return keycloak.token;
}

/** Interactive login only — used by landing CTAs and protected-route RequireAuth. */
export function loginToApp(redirectPath = "/dds"): void {
  const redirectUri = `${window.location.origin}${redirectPath}`;
  void initKeycloak()
    .catch(() => undefined)
    .then(() => keycloak.login(buildKeycloakLoginOptions(redirectUri)));
}

export default keycloak;
